import React, { useState } from 'react';
import PrivacyPolicyModal from './PrivacyPolicyModal';

const FeedbackForm = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    feedbackType: 'general',
    message: ''
  });
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          'form-name': 'feedback',
          ...formData
        }).toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          feedbackType: 'general',
          message: ''
        });
        setAcceptPrivacy(false);
        setTimeout(() => {
          onClose();
          setSubmitStatus(null);
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b-2 rounded-t-2xl" style={{ borderColor: '#E78E42' }}>
          <div className="p-6 pb-4" style={{ backgroundImage: 'linear-gradient(to right, rgba(231, 142, 66, 0.1), rgba(234, 193, 68, 0.1))' }}>
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-bold" style={{ color: '#000755' }}>
                  Share Your Feedback
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Help us improve the Solar Manufacturing Competitiveness Tool
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close feedback form"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} name="feedback" method="POST" data-netlify="true" className="p-6">
          <input type="hidden" name="form-name" value="feedback" />

          {/* Name field */}
          <div className="mb-4">
            <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 transition-colors"
              style={{ borderColor: '#E78E42' }}
              placeholder="Your name"
            />
          </div>

          {/* Email field */}
          <div className="mb-4">
            <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 transition-colors"
              style={{ borderColor: '#E78E42' }}
              placeholder="your.email@example.com"
            />
          </div>

          {/* Feedback Type */}
          <div className="mb-4">
            <label htmlFor="feedbackType" className="block text-sm font-semibold text-gray-700 mb-2">
              Feedback Type <span className="text-red-500">*</span>
            </label>
            <select
              id="feedbackType"
              name="feedbackType"
              value={formData.feedbackType}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 transition-colors"
              style={{ borderColor: '#E78E42' }}
            >
              <option value="general">General Feedback</option>
              <option value="bug">Bug Report</option>
              <option value="feature">Feature Request</option>
              <option value="data">Data Question</option>
              <option value="collaboration">Collaboration Inquiry</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Message field */}
          <div className="mb-6">
            <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
              Message <span className="text-red-500">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows="5"
              className="w-full px-4 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 transition-colors resize-none"
              style={{ borderColor: '#E78E42' }}
              placeholder="Please share your feedback, suggestions, or questions..."
            />
          </div>

          {/* Privacy Policy Acceptance */}
          <div className="mb-6">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={acceptPrivacy}
                onChange={(e) => setAcceptPrivacy(e.target.checked)}
                required
                className="mt-1 w-4 h-4 rounded border-2 focus:ring-2 cursor-pointer"
                style={{ borderColor: '#E78E42', accentColor: '#E78E42' }}
              />
              <span className="text-sm text-gray-700">
                I accept the{' '}
                <button
                  type="button"
                  onClick={() => setShowPrivacyPolicy(true)}
                  className="text-blue-600 hover:text-blue-800 underline font-semibold"
                >
                  Privacy Policy
                </button>
                {' '}and consent to the processing of my personal data as described.
                <span className="text-red-500"> *</span>
              </span>
            </label>
          </div>

          {/* Submit button */}
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={isSubmitting || !acceptPrivacy}
              className="flex-1 px-6 py-3 rounded-lg font-semibold text-white shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundImage: (isSubmitting || !acceptPrivacy) ? 'none' : 'linear-gradient(to right, #E78E42, #EAC144)', backgroundColor: (isSubmitting || !acceptPrivacy) ? '#ccc' : 'transparent' }}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Sending...
                </span>
              ) : (
                'Send Feedback'
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 rounded-lg font-semibold text-gray-700 bg-gray-200 hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
          </div>

          {/* Success/Error messages */}
          {submitStatus === 'success' && (
            <div className="mt-4 p-4 rounded-lg bg-green-50 border-2 border-green-400">
              <div className="flex items-center gap-2 text-green-800">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="font-semibold">Thank you! Your feedback has been submitted successfully.</span>
              </div>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mt-4 p-4 rounded-lg bg-red-50 border-2 border-red-400">
              <div className="flex items-center gap-2 text-red-800">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span className="font-semibold">Oops! Something went wrong. Please try again.</span>
              </div>
            </div>
          )}
        </form>
      </div>

      {/* Privacy Policy Modal */}
      <PrivacyPolicyModal
        isOpen={showPrivacyPolicy}
        onClose={() => setShowPrivacyPolicy(false)}
      />
    </div>
  );
};

export default FeedbackForm;
